import { config } from '_appConfig'

import { IPublicSsoConfig } from '../types'

export const MANUAL_LOGIN_PARAM = 'login'
export const MANUAL_LOGIN_VALUE = 'manual'

const AUTO_LOGIN_DISABLED_KEY = (portalId: string) => `portalAutoLoginDisabled_${portalId}`
const AUTO_LOGIN_TAB_BYPASS_KEY = (portalId: string) => `portalAutoLoginBypass_${portalId}`

// 5-minute TTL — enough for the typical "switch accounts" flow without
// persistently disabling auto-login on the device.
const AUTO_LOGIN_BYPASS_TTL_MS = 5 * 60 * 1000

const safeStorageOp = <T>(fn: () => T): T | undefined => {
  try {
    return fn()
  } catch {
    // Quota / privacy mode / disabled storage — ignore.
    return undefined
  }
}

const readSearchParams = (): URLSearchParams => {
  const searchStr = window.location.search || window.location.hash.split('?')[1] || ''
  return new URLSearchParams(searchStr)
}

const hasManualLoginParam = (): boolean =>
  readSearchParams().get(MANUAL_LOGIN_PARAM) === MANUAL_LOGIN_VALUE

export const isAutoLoginBypassed = (portalId: string): boolean => {
  if (hasManualLoginParam()) {
    safeStorageOp(() => window.sessionStorage.setItem(AUTO_LOGIN_TAB_BYPASS_KEY(portalId), '1'))
    return true
  }

  if (
    safeStorageOp(() => window.sessionStorage.getItem(AUTO_LOGIN_TAB_BYPASS_KEY(portalId))) === '1'
  ) {
    return true
  }

  const until = safeStorageOp(() => window.localStorage.getItem(AUTO_LOGIN_DISABLED_KEY(portalId)))
  if (until && Date.now() < Number(until)) {
    return true
  }
  if (until) {
    safeStorageOp(() => window.localStorage.removeItem(AUTO_LOGIN_DISABLED_KEY(portalId)))
  }
  return false
}

export const markAutoLoginBypassedAfterLogout = (portalId: string): void => {
  safeStorageOp(() =>
    window.localStorage.setItem(
      AUTO_LOGIN_DISABLED_KEY(portalId),
      String(Date.now() + AUTO_LOGIN_BYPASS_TTL_MS),
    ),
  )
}

export const getAutoLoginConfig = (
  ssoConfigs: IPublicSsoConfig[] | undefined,
): IPublicSsoConfig | undefined => ssoConfigs?.find((c) => c.enabled && c.autoLogin)

export const buildSsoAuthorizeUrl = (
  companyId: string,
  portalId: string,
  configId: string,
  returnUrl?: string,
  options?: { manual?: boolean },
): string => {
  const base = `${config.io.contentspaceEndpoint}/public/companies/${companyId}/portals/${portalId}/sso/authorize`
  const params = new URLSearchParams({ configId })
  if (returnUrl) {
    params.set('next', returnUrl)
  }
  // Маркер мануального клика — BE по нему пропускает auto-login loop guard
  // (см. isSuspectedAutoLoginLoop в sso.public.ts). Семантика — OAuth-стандартный
  // prompt=login: пользователь явно инициировал re-auth, петлёй это быть не может.
  if (options?.manual) {
    params.set('prompt', 'login')
  }
  return `${base}?${params.toString()}`
}

export const appendManualLoginParam = (url: string): string => {
  if (url.includes(`${MANUAL_LOGIN_PARAM}=${MANUAL_LOGIN_VALUE}`)) {
    return url
  }
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}${MANUAL_LOGIN_PARAM}=${MANUAL_LOGIN_VALUE}`
}
